import { TRADE, type TradeGoodId } from '@sd/content';
import type { PlanetState } from './planet';
import { NO_TECH, type TechMods } from './techs';

/**
 * Contracts and caravans (GDD §6). A contract is proposed by one player and
 * accepted by the other; the seller then assigns a caravan, which loads at
 * the seller's capital, flies to the buyer, unloads and collects the price
 * (or the barter goods), and flies back — once per delivery.
 */
export type CaravanPhase = 'atSeller' | 'toBuyer' | 'toSeller';

export interface Caravan {
  readonly fleetId: number;
  readonly phase: CaravanPhase;
  /** Goods on board right now. */
  readonly cargo: Readonly<Partial<Record<TradeGoodId, number>>>;
  /** Players whose tariff this caravan already paid on the current leg (GDD §6). */
  readonly tolled?: readonly string[];
}

export interface Contract {
  readonly id: number;
  readonly seller: string;
  readonly buyer: string;
  readonly proposedBy: string;
  readonly status: 'proposed' | 'active';
  /** What the seller delivers each time. */
  readonly good: TradeGoodId;
  readonly quantity: number;
  /** ГС per unit, or null for a barter deal. */
  readonly price: number | null;
  /** What the buyer hands back each time in a barter deal. */
  readonly barter: { readonly good: TradeGoodId; readonly quantity: number } | null;
  readonly deliveriesLeft: number;
  /** Deliveries in a row the seller could not load. */
  readonly missed: number;
  readonly caravan: Caravan | null;
}

/** Contract slots for a commerce center level (GDD §5: contracts come with it). */
export function contractSlots(planet: PlanetState, mods: TechMods = NO_TECH): number {
  const level = Math.max(
    0,
    ...planet.buildings.filter((b) => b.id === 'commerceCenter').map((b) => b.level),
  );
  if (level === 0) return 0;
  return (TRADE.slotsByCommerceLevel[Math.min(level, 3) - 1] ?? 0) + mods.contractSlots;
}

export const involves = (c: Contract, player: string): boolean =>
  c.seller === player || c.buyer === player;

export const cargoTotal = (cargo: Caravan['cargo']): number =>
  Object.values(cargo).reduce<number>((a, b) => a + (b ?? 0), 0);
